<script>
import store from '@state/store'
import { Cropper } from 'vue-advanced-cropper'
import { validationMixin } from 'vuelidate'
import { required, not, sameAs } from 'vuelidate/lib/validators'

export default {
  name: 'ProfileTab',
  components: {
    Cropper,
  },
  mixins: [validationMixin],
  props: {
    user: {
      type: Object,
      required: true,
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userName: this.user.name,
      userEmail: this.user.email,
      userPhone: this.user.phone,
      userCountryPrefix: this.user.country_prefix,
      userPhoto: this.user.image_url,
      editingName: false,
      editingEmail: false,
      editingPassword: false,
      toCropImage: false,
      visibleImageCropper: false,
      croppedImage: false,
      userCurrentPassword: '',
      userNewPassword: '',
      userConfirmNewPassword: '',
      saveButtonText: 'Save',
    }
  },
  validations: {
    userCurrentPassword: {
      required,
    },
    userNewPassword: {
      required,
      notCurrentPassword: not(sameAs('userCurrentPassword')),
    },
    userConfirmNewPassword: {
      required,
      sameAsNewPassword: sameAs('userNewPassword'),
    },
  },
  computed: {},
  methods: {
    saveUserEdits() {
      this.saveButtonText = 'Loading...'
      var data = {
        name: this.userName,
        email: this.userEmail,
      }
      if (this.editingPassword) {
        this.$v.$touch()
        // if its still pending or an error is returned do not submit
        if (this.$v.$pending || this.$v.$error) return
        // to form submit after this

        data.password = this.userCurrentPassword
        data.new_password = this.userNewPassword
        data.verify_password = this.userConfirmNewPassword
      }
      if (this.userPhoto) {
        data.image_url = this.userPhoto
      }
      store
        .dispatch('auth/updateUser', {
          data: data,
        })
        .then((response) => {
          this.editingName = false
          this.editingEmail = false
          this.editingPassword = false
          this.$emit('close')
          this.saveButtonText = 'Save'
        })
    },
    editName() {
      this.editingName = true
    },
    saveName() {
      this.editingName = false
    },
    cancelName() {
      this.userEmail = this.user.email
      this.editingName = false
    },
    editEmail() {
      this.editingEmail = true
    },
    saveEmail() {
      this.editingEmail = false
    },
    cancelEmail() {
      this.userEmail = this.user.email
      this.editingEmail = false
    },
    clickOnFileUpload() {
      this.$refs.file.click()
    },
    resetChanges() {
      this.userEmail = this.user.email
      this.editingName = false
      this.userEmail = this.user.email
      this.editingEmail = false
      this.userPhone = this.user.phone
      this.editingPhone = false
      this.userPhoto = this.user.image_url
      this.editingPassword = false
      this.$emit('close')
    },
    uploadFiles(event) {
      var input = event.target
      if (input.files && input.files.length > 0) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.toCropImage = {
            original: e.target.result,
            cropped: null,
          }
        }
        reader.readAsDataURL(input.files[0])
        this.visibleImageCropper = true
      }
    },
    crop() {
      const { coordinates, canvas } = this.$refs.cropper.getResult()
      this.coordinates = coordinates
      this.toCropImage.cropped = canvas.toDataURL()
    },
    onClose() {
      this.visibleImageCropper = false
    },
    finishCropping() {
      this.crop()
      this.croppedImage = this.toCropImage.cropped
      this.userPhoto = this.croppedImage
      this.visibleImageCropper = false
    },
    editPassword() {
      this.editingPassword = !this.editingPassword
    },
  },
}
</script>

<template>
  <div>
    <div class="flex justify-between">
      <div class="w-7/12">
        <div class="mb-7">
          <label for="name" class="text-base text-gray-light">Name:</label>
          <span
            v-show="!editingName"
            class="ml-5 text-sm cursor-pointer text-blue"
            @click="editName"
            >Edit</span
          >
          <span
            v-show="editingName"
            class="ml-5 text-sm cursor-pointer text-blue"
            @click="saveName"
            >Save</span
          >
          <span
            v-show="editingName"
            class="ml-5 text-sm cursor-pointer text-blue"
            @click="cancelName"
            >Cancel</span
          >
          <br />
          <div v-show="!editingName" class="text-3xl text-blue-darker">{{
            userName
          }}</div>
          <input
            v-show="editingName"
            v-model="userName"
            class="w-10/12 input"
            type="text"
            name="name"
          />
        </div>
        <div class="mb-7">
          <label for="email" class="text-base text-gray-light">Email:</label>
          <span
            v-show="!editingEmail"
            class="ml-5 text-sm cursor-pointer text-blue"
            @click="editEmail"
            >Edit</span
          >
          <span
            v-show="editingEmail"
            class="ml-5 text-sm cursor-pointer text-blue"
            @click="saveEmail"
            >Save</span
          >
          <span
            v-show="editingEmail"
            class="ml-5 text-sm cursor-pointer text-blue"
            @click="cancelEmail"
            >Cancel</span
          >
          <br />
          <div v-show="!editingEmail" class="text-3xl text-blue-darker">{{
            userEmail
          }}</div>
          <input
            v-show="editingEmail"
            v-model="userEmail"
            class="w-10/12 input"
            type="email"
            name="email"
          />
        </div>
        <div class="mb-7">
          <label for="phone" class="text-base text-gray-light">Phone:</label>
          <router-link
            :to="{ name: 'verify-phone' }"
            class="ml-5 text-sm cursor-pointer text-blue"
            >Edit</router-link
          ><br />
          <div v-show="!editingPhone" class="text-3xl text-blue-darker"
            >{{ userCountryPrefix }} - {{ userPhone }}</div
          >
        </div>

        <div v-show="editingPassword" class="w-10/12 space-y-6">
          <div>
            <label class="block mb-2 text-base text-gray-light">
              Current Password:
            </label>
            <BaseInputText
              v-model="userCurrentPassword"
              :vuelidate="$v.userCurrentPassword"
              required
              type="password"
            />
          </div>
          <div>
            <label class="block mb-2 text-base text-gray-light">
              New Password:
            </label>
            <BaseInputText
              v-model="userNewPassword"
              :vuelidate="$v.userNewPassword"
              type="password"
              required
            >
              <template v-slot:errors>
                <p
                  v-if="
                    $v.userNewPassword.required &&
                      !$v.userNewPassword.notCurrentPassword
                  "
                >
                  New password must be different.
                </p>
              </template>
            </BaseInputText>
          </div>
          <div>
            <label class="block mb-2 text-base text-gray-light">
              Confirm New Password:
            </label>
            <BaseInputText
              v-model="userConfirmNewPassword"
              :vuelidate="$v.userConfirmNewPassword"
              type="password"
              required
              class="mb-6"
            >
              <template v-slot:errors>
                <p
                  v-if="
                    $v.userConfirmNewPassword.required &&
                      !$v.userConfirmNewPassword.sameAsNewPassword
                  "
                >
                  Passwords must be identical.
                </p>
              </template>
            </BaseInputText>
          </div>
        </div>

        <div class="">
          <button
            class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mt-3"
            @click="saveUserEdits"
            >{{ saveButtonText }}</button
          >
          <button
            class="button secondary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mt-3"
            @click="resetChanges"
            >Cancel</button
          >
        </div>
      </div>
      <div class="w-5/12">
        <div class="mb-11">
          <label for="name" class="text-base text-gray-light">Avatar:</label>
          <br />
          <div class="flex">
            <div class="relative w-6/12">
              <img
                :src="userPhoto"
                class="mt-3.5 rounded-full h-36 w-36 inline-block "
              />
              <div
                class="border border-blue-default rounded-full absolute bottom-0 right-0 p-2.5 bg-white w-10 h-10 cursor-pointer"
                @click="clickOnFileUpload"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="19.494"
                  height="16.611"
                  viewBox="0 0 19.494 16.611"
                >
                  <g
                    id="Group_336"
                    data-name="Group 336"
                    transform="translate(-3 -2)"
                  >
                    <path
                      id="Path_180"
                      data-name="Path 180"
                      d="M19.774,18.611H5.72A2.72,2.72,0,0,1,3,15.891V8.347a2.72,2.72,0,0,1,2.72-2.72H7.08l.453-1.655A3.239,3.239,0,0,1,10.707,2h4.08a3.239,3.239,0,0,1,3.173,1.972l.453,1.655h1.36a2.72,2.72,0,0,1,2.72,2.72v7.544A2.72,2.72,0,0,1,19.774,18.611ZM5.72,6.534A1.813,1.813,0,0,0,3.907,8.347v7.544A1.813,1.813,0,0,0,5.72,17.7H19.774a1.813,1.813,0,0,0,1.813-1.813V8.347a1.813,1.813,0,0,0-1.813-1.813H18.06a.454.454,0,0,1-.453-.331l-.553-1.986a2.323,2.323,0,0,0-2.267-1.31h-4.08A2.323,2.323,0,0,0,8.44,4.217L7.892,6.2a.454.454,0,0,1-.453.331Z"
                    />
                    <path
                      id="Path_181"
                      data-name="Path 181"
                      d="M19.08,23.16a4.08,4.08,0,1,1,4.08-4.08,4.08,4.08,0,0,1-4.08,4.08Zm0-7.254a3.173,3.173,0,1,0,3.173,3.173A3.173,3.173,0,0,0,19.08,15.907Z"
                      transform="translate(-6.56 -7.106)"
                    />
                    <path
                      id="Path_182"
                      data-name="Path 182"
                      d="M35.36,16.72a1.36,1.36,0,1,1,1.36-1.36A1.36,1.36,0,0,1,35.36,16.72Zm0-1.813a.453.453,0,1,0,.453.453A.454.454,0,0,0,35.36,14.907Z"
                      transform="translate(-16.946 -6.56)"
                    />
                  </g>
                </svg>
              </div>
            </div>
            <div class="relative w-5/12">
              <button
                class="button secondary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mb-1 absolute bottom-0"
                @click="clickOnFileUpload"
                >Upload</button
              >
              <input
                ref="file"
                type="file"
                class="hidden"
                multiple
                accept="image/*,video/*"
                @change="uploadFiles($event)"
              />
              <BaseModalWindow
                modalcls="w-user-edit bg-white rounded-xl mx-auto px-11 shadow-box relative z-40 left-0 flex justify-between overflow-hidden"
                :visible="visibleImageCropper"
                @close="onClose"
              >
                <template v-slot:body>
                  <div class="w-full mx-auto mt-12 mb-5">
                    <div class="">
                      <Cropper
                        v-if="toCropImage"
                        ref="cropper"
                        :stencil-props="{
                          aspectRatio: 1 / 1,
                        }"
                        classname="w-full"
                        :src="toCropImage.original"
                      />
                    </div>

                    <div class="mt-3">
                      <button
                        class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
                        @click="finishCropping"
                        >Done</button
                      >
                    </div>
                  </div>
                </template>
              </BaseModalWindow>
            </div>
          </div>
        </div>
        <div class="">
          <router-link
            v-if="isInModal"
            :to="{ name: 'user-page', params: { userId: user.id } }"
            class="inline-block h-12 mr-2 text-right button primary-button"
            >View Profile</router-link
          >

          <button
            class="button secondary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mt-3"
            @click="editPassword"
            >Change Password</button
          >
        </div>
      </div>
    </div>
  </div>
</template>
